import { useAdjustPricelistForm } from '@/forms/adjust-pricelist.form';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { DialogProps } from '@components/dialogs/dialogs-provider';
import { useGlobalStore } from '@/stores';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

const DELIVERY_TIMES = Array.from({ length: 25 })
  .map((_, index) => {
    const hour = Math.floor(index / 2) + 6;
    const minute = index % 2 === 0 ? '00' : '30';
    const time = `${hour.toString().padStart(2, '0')}:${minute}`;
    return time;
  })
  .filter((time) => time >= '06:00' && time <= '18:00');

const WEEKDAYS = [
  { value: 'monday', key: 'monday' },
  { value: 'tuesday', key: 'tuesday' },
  { value: 'wednesday', key: 'wednesday' },
  { value: 'thursday', key: 'thursday' },
  { value: 'friday', key: 'friday' },
];

export function AdjustPricelistDialog({
  open,
  onClose,
}: DialogProps<undefined, string | null>) {
  const { t } = useTranslation('common');
  const { enqueueSnackbar } = useSnackbar();
  const { contactEmployeeId } = useGlobalStore();
  const {
    register,
    onSubmit,
    watch,
    setValue,
    isLoading,
    isSubmitting,
    formState: { errors: formErrors },
  } = useAdjustPricelistForm({
    contactEmployeeId,
    onSuccess: () => {
      enqueueSnackbar(t('dialogs.adjustPricelist.success'), {
        variant: 'success',
      });
      onClose(null);
    },
    onError: () => {
      enqueueSnackbar(t('dialogs.adjustPricelist.error'), {
        variant: 'error',
      });
    },
  });

  const isEnabled = watch('isEnabled');
  const deliveryDays = watch('deliveryDays');
  const deliveryTime = watch('deliveryTime');

  const handleWeekdayToggle = (day: (typeof WEEKDAYS)[number]['value']) => {
    const currentDays = deliveryDays || [];
    const newDays = currentDays.includes(day as any)
      ? currentDays.filter((d) => d !== day)
      : [...currentDays, day];
    setValue('deliveryDays', newDays as any[]);
  };

  return (
    <Dialog
      component="form"
      onSubmit={onSubmit}
      fullWidth
      open={open}
      onClose={() => onClose(null)}
    >
      <DialogTitle>{t('dialogs.adjustPricelist.title')}</DialogTitle>
      <DialogContent>
        <DialogContentText gutterBottom>
          {t('dialogs.adjustPricelist.description')}
        </DialogContentText>
        <Stack spacing={3} sx={{ py: 2 }}>
          <FormControlLabel
            control={
              <Checkbox {...register('isEnabled')} checked={isEnabled} />
            }
            label={t('dialogs.adjustPricelist.fields.enable')}
          />

          {isEnabled && (
            <>
              <FormControl>
                <FormLabel>
                  {t('dialogs.adjustPricelist.fields.deliveryTime')}
                </FormLabel>
                <Select
                  {...register('deliveryTime')}
                  size="small"
                  sx={{ mt: 1 }}
                  value={deliveryTime}
                  MenuProps={{
                    PopoverClasses: {
                      root: 'MuiPopover-root-high-z-index',
                    },
                  }}
                >
                  {DELIVERY_TIMES.map((time) => (
                    <MenuItem key={time} value={time}>
                      {time} {t('dialogs.adjustPricelist.fields.hour')}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>
                  {t('dialogs.adjustPricelist.fields.deliveryDays')}
                </FormLabel>
                <FormGroup sx={{ mt: 1 }}>
                  {WEEKDAYS.map(({ value, key }) => (
                    <FormControlLabel
                      key={value}
                      control={
                        <Checkbox
                          checked={deliveryDays?.includes(value as any)}
                          onChange={() => handleWeekdayToggle(value)}
                        />
                      }
                      label={t(`dialogs.adjustPricelist.weekdays.${key}`)}
                    />
                  ))}
                </FormGroup>
              </FormControl>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(null)} disabled={isSubmitting}>
          {t('actions.cancel')}
        </Button>
        <Button type="submit" disabled={isLoading || isSubmitting}>
          {t('actions.save')}
        </Button>
      </DialogActions>
      <style>
        {`
          .MuiPopover-root-high-z-index {
            z-index: 9999 !important;
          }
        `}
      </style>
    </Dialog>
  );
}
